import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Grid, Box, Paper, Typography} from "@mui/material";
import PersonalDetails from "../CV/Steps/PersonalDetails";
import Education from "../CV/Steps/Education";
import Experience from "../CV/Steps/Experience";
import ManualScorePersonalDetails from "./ManualScoring/ManualScorePersonalDetails";
import ManualScoreEducation from "./ManualScoring/ManualScoreEducation";
import ManualScoreExperience from "./ManualScoring/ManualScoreExperience";
import Skills from "../CV/Steps/Skills";
import ManualScoreSkills from "./ManualScoring/ManualScoreSkills";
import AdditionalQuestions from "../CV/Steps/AdditionalQuestions";
import ManualScoreAdditionalQuestions from "./ManualScoring/ManualScoreAdditionalQuestions";
import ManualScoreApplicationDetails from "./ManualScoring/ManualScoringApplicationDetails";
import Enums from "../General/Enums";
import {findById, scoreCV} from "../../utilities/cvAPI";
import keycloak from "../../Keycloak";
import Button from "@mui/material/Button";
import {toast} from "react-toastify";
import Modal from "@mui/material/Modal";

export default function ApplicationScoring({ candidateId, onScoreUpdate, status }) {
    const [cv, setCV] = useState(Enums.cv);
    const [displayApplication, setDisplayApplication] = useState(false);
    const [dataFetched, setDataFetched] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showModal, setShowModal] = useState(false);

    const disabledStyle = useMemo(() => ({
        pointerEvents: "none",
        opacity: 0.6,
    }), []);

    const buttonStyle = useMemo(() => ({
        width: '89px',
        height: '40px',
        fontFamily: 'Poppins',
        color: '#ffffff',
        borderColor: '#FF6600',
    }), []);

    const showSaveSuccess = useCallback((message) => {
        toast.success(message, {
            position: toast.POSITION.TOP_LEFT,
            style: {
                backgroundColor: "#49c96d",
                width: "650px",
                color: "#FFFFFF",
                left: "500px",
                theme: "colored",
            },
        });
    }, []);

    const updateScore = useCallback((section, data) => {
        setCV((prevScore) => ({
            ...prevScore,
            scoreSummary: {
                ...prevScore.scoreSummary,
                [section]: data,
            },
        }));
    }, []);

    const calculatedScores = useMemo(() => {
        const calculateSectionScore = (section, weight) => {
            let sectionScore = 0;
            for (let key in cv.scoreSummary[section]) {
                if (typeof cv.scoreSummary[section][key] === "boolean" || cv.scoreSummary.experienceScore[key] === "on") {
                    sectionScore += cv.scoreSummary[section][key] ? 2 : 1;
                } else {
                    sectionScore += cv.scoreSummary[section][key];
                }
            }
            return ((sectionScore / weight) * 100);
        };

        const additionalQuestionsScore = calculateSectionScore('additionalQuestionsScore', 8);
        const personalDetailsScore = calculateSectionScore('personalDetailsScore', 12);
        const educationScore = calculateSectionScore('educationScore', 20);
        const experienceScore = calculateSectionScore('experienceScore', 64);
        const skillsScore = calculateSectionScore('skillsScore', 40);
        const applicationDetailsScore = calculateSectionScore('applicationDetailsScore', 24);

        const totalScore = Math.ceil(
            (additionalQuestionsScore * 0.2) +
            (personalDetailsScore * 0.05) +
            (educationScore * 0.2) +
            (experienceScore * 0.25) +
            (skillsScore * 0.25) +
            (applicationDetailsScore * 0.05)
        );

        return {
            additionalQuestionsScore,
            personalDetailsScore,
            educationScore,
            experienceScore,
            skillsScore,
            applicationDetailsScore,
            totalScore,
        };
    }, [cv.scoreSummary]);

    useEffect(() => {
        setCV((prevCv) => ({
            ...prevCv,
            scoreSummary: {
                ...prevCv.scoreSummary,
                score: calculatedScores.totalScore,
            },
        }));
    }, [calculatedScores.totalScore]);

    const handleClick = useCallback(() => {
        onScoreUpdate(cv.scoreSummary.score);
        scoreCV(cv.id, cv.scoreSummary, keycloak.token)
            .then(() => {
                showSaveSuccess("CV score successfully updated");
                window.scrollTo(0, 0);
            })
            .catch(() => console.log("Could not update CV"));
        setShowModal(false);
    }, [cv, onScoreUpdate, showSaveSuccess]);

    const callCandidateByCvId = useCallback(async () => {
        try {
            const cvData = await findById(candidateId, keycloak.token);
            if (cvData) {
                setCV(cvData);
                setDataFetched(true);
            }
        } catch (e) {
            setDisplayApplication(false);
            setErrorMessage("Candidate application not found");
            console.log(e);
        }
    }, [candidateId]);

    useEffect(() => {
        if ((keycloak.hasResourceRole("hr_admin") || keycloak.hasResourceRole("client_recruiter")) && candidateId && !dataFetched) {
            callCandidateByCvId();
        }
    }, [candidateId, callCandidateByCvId, dataFetched]);

    const isDisabled = status !== 'AWAITING_SCORE';

    return (
        <div>
            <Modal
                data-testid="confirmSave-modal"
                open={showModal}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={Enums.modalStyle}>
                    <Typography variant="h5" component="h2" align="left" gutterBottom>
                        Save Score
                    </Typography>
                    <Typography align="left" paragraph>
                        Are you sure you want to save this score? Once saved, you cannot go back and update the score.
                    </Typography>
                    <Grid container justifyContent="right" spacing={2} mt={2}>
                        <Grid item>
                            <Button
                                variant="outlined"
                                data-testid="cancel"
                                onClick={() => setShowModal(false)}
                                sx={{
                                    ...buttonStyle,
                                    color: '#FF6600',
                                    borderColor: '#FF6600',
                                    '&:hover': {
                                        borderColor: '#FF6600'
                                    },
                                    '&:focus': {
                                        borderColor: '#FF6600'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={handleClick}
                                data-testid="save"
                                sx={{
                                    ...buttonStyle,
                                    backgroundColor: '#FF6600',
                                    '&:hover': {
                                        backgroundColor: '#FF6600'
                                    },
                                    '&:focus': {
                                        backgroundColor: '#FF6600'
                                    }
                                }}
                            >
                                Save
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>

            {dataFetched ? (
                <Box>
                    <Grid container direction="row" data-testid="scoringGrid" spacing={3}>
                        <Grid item xs={9}>
                            <Paper elevation={0} sx={{ pointerEvents: 'none' }}>
                                <PersonalDetails personalDetails={cv.personalDetails} readOnly />
                            </Paper>
                        </Grid>

                        <Grid item xs={3} sx={isDisabled ? disabledStyle : {}}>
                            <ManualScoreApplicationDetails updateScore={updateScore} cvScore={cv.scoreSummary?.applicationDetailsScore} />
                            <ManualScorePersonalDetails updateScore={updateScore} cvScore={cv.scoreSummary?.personalDetailsScore} />
                        </Grid>

                        <Grid item xs={9}>
                            <Paper elevation={0} sx={{ pointerEvents: 'none' }}>
                                <Education education={cv.education} readOnly />
                            </Paper>
                        </Grid>

                        <Grid item xs={3} sx={isDisabled ? disabledStyle : {}}>
                            <ManualScoreEducation updateScore={updateScore} cvScore={cv.scoreSummary?.educationScore} />
                        </Grid>

                        <Grid item xs={9}>
                            <Paper elevation={0} sx={{ pointerEvents: 'none' }}>
                                <Experience experience={cv.experience} readOnly />
                            </Paper>
                        </Grid>

                        <Grid item xs={3} sx={isDisabled ? disabledStyle : {}}>
                            <ManualScoreExperience updateScore={updateScore} cvScore={cv.scoreSummary?.experienceScore} noExperience={cv.experience[0].graduate} />
                        </Grid>

                        <Grid item xs={9}>
                            <Paper elevation={0} sx={{ pointerEvents: 'none' }}>
                                <Skills skills={cv.skills} readOnly />
                            </Paper>
                        </Grid>

                        <Grid item xs={3} sx={isDisabled ? disabledStyle : {}}>
                            <ManualScoreSkills updateScore={updateScore} cvScore={cv.scoreSummary?.skillsScore} />
                        </Grid>

                        <Grid item xs={9}>
                            <Paper elevation={0} sx={{ pointerEvents: 'none' }}>
                                <AdditionalQuestions additionalQuestions={cv.additionalQuestions} readOnly />
                            </Paper>
                        </Grid>

                        <Grid item xs={3} sx={isDisabled ? disabledStyle : {}}>
                            <ManualScoreAdditionalQuestions updateScore={updateScore} cvScore={cv.scoreSummary?.additionalQuestionsScore} />

                            <Grid container direction="column" alignItems="center">
                                <Grid item xs={12}>
                                    <Typography variant="h6" sx={{ color: "#FF6600" }}>
                                        Total Score: {calculatedScores?.totalScore || 0}%
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={() => setShowModal(true)}
                                        data-testid="openSaveModal"
                                        disabled={isDisabled}
                                        sx={{
                                            ...buttonStyle,
                                            backgroundColor: isDisabled ? '#B0B0B0' : '#FF6600',
                                            '&:hover': {
                                                backgroundColor: '#FF6600'
                                            },
                                            '&:focus': {
                                                backgroundColor: '#FF6600'
                                            }
                                        }}
                                    >
                                        Save
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                <h5
                    id="missingIdNumber"
                    hidden={displayApplication}
                    style={{ marginLeft: "40px", marginTop: "40px" }}
                >
                    {errorMessage}
                </h5>
            )}
        </div>
    );
}